import React, { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useGetNotificationEventsQuery } from "../../../app/api/console/consoleCustomerAnalyticsApiSlice";
import SendDetailsPanel from "./SendDetailsPanel";
import { Card, CardHeader, Donut, EmptyState, Loading, Reading, StatCard } from "./ui";
import { firePathMix, notificationTotals, notificationsOverTime, splitNotifications } from "./helpers";

// Run-complete emails: the summary uses the page range; Send Details has its own period.
export default function EmailsTab({ timeRange, useProd, websiteNames, onInspect }) {
  const { data: source = [], isLoading, error } = useGetNotificationEventsQuery({ limit: 1000, timeRange, useProd });
  const { sends, skips } = useMemo(() => splitNotifications(source), [source]);
  const totals = useMemo(() => notificationTotals(sends), [sends]);
  const overTime = useMemo(() => notificationsOverTime(sends), [sends]);
  const firePaths = useMemo(() => firePathMix(sends), [sends]);
  const env = useProd ? "production" : "staging";

  return (
    <div className='space-y-6' data-testid='tab-emails'>
      {isLoading ? (
        <Loading label='Loading notification sends…' />
      ) : error ? (
        <EmptyState title={`Notification telemetry unavailable in ${env}`}>
          The {env} Platform API does not expose <code>/notifications/run-announcement/events</code> yet.
        </EmptyState>
      ) : sends.length === 0 && skips.length === 0 ? (
        <EmptyState title={`No run notifications sent in the last ${timeRange}`}>
          Emails fire on run settle, Console "Send notification", staging→prod push, and MCP <code>notify_run</code>. Send Details below can look further back.
        </EmptyState>
      ) : (
        <>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3.5'>
            <StatCard label='Emails sent' value={totals.sends} note={`last ${timeRange}`} labelColor='text-teal-700' />
            <StatCard label='Deliveries' value={totals.deliveries} note={`${totals.uniqueRecipients} unique recipients`} color='text-accent-purple' labelColor='text-accent-purple' />
            <StatCard label='Issue-bearing' value={totals.issueBearing} note='highest-priority finding included' color='text-severity-high' labelColor='text-severity-high' />
            <StatCard label='All clear' value={totals.allClear} note='every check passed' color='text-accent-green' labelColor='text-accent-green' />
            <StatCard label='Runs covered' value={totals.uniqueRuns} note={`${skips.length} skipped attempts`} />
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            <Card className='flex flex-col'>
              <CardHeader
                title='Sends over time'
                badge={
                  <span className='flex gap-3 font-semibold uppercase tracking-wider'>
                    <span className='flex items-center gap-1.5'><span className='w-2.5 h-2.5 rounded-xs bg-[#8B5CF6]' /> Issue-bearing</span>
                    <span className='flex items-center gap-1.5'><span className='w-2.5 h-2.5 rounded-xs bg-[#1FAF5C]' /> All clear</span>
                  </span>
                }
              />
              <div className='h-52'>
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart data={overTime}>
                    <CartesianGrid strokeDasharray='3 3' vertical={false} />
                    <XAxis dataKey='date' tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey='Issue-bearing' stackId='n' fill='#8B5CF6' opacity={0.85} />
                    <Bar dataKey='All clear' stackId='n' fill='#1FAF5C' opacity={0.85} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <Reading>volume should track run settles; a silent stretch with active runs means the settle fire path is broken.</Reading>
            </Card>

            <Card className='flex flex-col'>
              <CardHeader title='Fire path breakdown' badge={`${totals.sends} sends`} hint='How each email was triggered: automatic run settle, Console manual send, staging→prod push, or MCP notify_run.' />
              <Donut data={firePaths} total={totals.sends} unit='sends' mono />
            </Card>
          </div>
        </>
      )}

      <SendDetailsPanel useProd={useProd} websiteNames={websiteNames} onInspect={onInspect} />
    </div>
  );
}
